import styled from '@emotion/styled';
import { RacemapColors } from '@racemap/utilities/consts/common';
import { getPricingRulesOfEvent } from '@racemap/utilities/functions/billing';
import { getEventCyclesOfTimeRange } from '@racemap/utilities/functions/billing/getEventCyclesOfTimeRange';
import { Alert, Button } from 'antd';
import type { FC, ReactNode } from 'react';
import { toast } from 'react-toastify';
import { AsyncButton } from 'src/components/AsyncButton';
import { useCurrentEvent } from 'src/lib/customHooks/useCurrentEvent';
import { useCurrentUser } from 'src/lib/customHooks/useCurrentUser';
import { useEventBasePrice } from 'src/lib/customHooks/useEventBasePrice';
import { useIsChildAccount } from 'src/lib/customHooks/useIsChildAccount';
import { useIsReseller } from 'src/lib/customHooks/useIsReseller';
import { useIsSafemap } from 'src/lib/customHooks/useIsSafemap';
import { useUserCanActivateDirectly } from 'src/lib/customHooks/useUserCanActivateDirectly';
import { formatPrice } from 'src/misc/formatPrice';
import { useUser } from '../../../lib/customHooks/index';
import { getBillableEventsForEvent } from '../../../store/events/events_reducer_helper';
import { FormCheckText } from '../../BasicComponents/Form/FormCheckText';
import {
  AnalyticsTabLink,
  BillingLink,
  CostCalculatorLink,
  PricingLink,
} from '../../BasicComponents/Links/index';
import { HStack } from '../../BasicComponents/MetaComponent/index';
import { PopoverHint } from '../../BasicComponents/PopoverHint/index';
import { IconArrowRight, IconCreditCard } from '../../Icon';
import { EventCycleHint } from './EventCycleHint';
import { PayLaterList } from './PayLaterList';

interface Props {
  onClose: () => void;
  onNext: () => Promise<void>;
  checks: Checks;
  onChecksChange: (newValue: Checks) => void;
}

export interface Checks {
  acceptTerms: boolean;
  acceptSubscription: boolean;
  canActivateDirectlyFetched: boolean;
}

export interface ViewSlots {
  title: ReactNode;
  body: ReactNode;
  footer: ReactNode;
}

export const useOverviewView = ({
  onClose,
  onNext,
  checks,
  onChecksChange,
}: Props): ViewSlots | null => {
  const currentUser = useCurrentUser();
  const currentEvent = useCurrentEvent();
  const isReseller = useIsReseller();
  const isChildAccount = useIsChildAccount();

  if (currentUser == null || currentEvent == null) return null;

  const acceptedChecks =
    checks.acceptTerms && checks.acceptSubscription && checks.canActivateDirectlyFetched;

  const handleNextClick = async () => {
    if (!acceptedChecks) return;
    await onNext();
  };

  return {
    title: '1. Activate event',
    body: (
      <ModalBody>
        {isChildAccount && <EventCycleInfo />}
        <Disclaimer />
        {!isReseller && !isChildAccount && <PayNowList />}
        <FurtherInformation userId={currentUser.id} eventId={currentEvent.id} />
        {!isChildAccount && <PayLaterList event={currentEvent} />}

        <Checks value={checks} onChange={onChecksChange} />
      </ModalBody>
    ),
    footer: (
      <OverviewViewFooter
        onNextClick={handleNextClick}
        onCancelClick={onClose}
        blocked={!acceptedChecks}
      />
    ),
  };
};

interface FooterProps {
  onNextClick: () => Promise<void>;
  onCancelClick: () => void;
  blocked: boolean;
}

const OverviewViewFooter: FC<FooterProps> = ({ onNextClick, onCancelClick, blocked }) => {
  const currentEvent = useCurrentEvent();
  const currentUser = useCurrentUser();
  const { canActivateDirectly, isLoading } = useUserCanActivateDirectly(
    currentUser?.id || undefined,
  );

  if (currentEvent == null || currentUser == null) return <></>;
  const nextLabel = getNextButtonLabel(canActivateDirectly);

  const handleNextClick = async () => {
    try {
      await onNextClick();
    } catch (e) {
      if (e instanceof Error) {
        toast.error(e.message);
        throw e;
      }
    }
  };

  return (
    <HStack gap="5px">
      <Button onClick={onCancelClick} variant="outlined">
        Cancel
      </Button>
      <AsyncButton type="primary" onClick={handleNextClick} disabled={blocked} loading={isLoading}>
        {nextLabel}
      </AsyncButton>
    </HStack>
  );
};

const getNextButtonLabel = (canActivateDirectly: boolean | undefined) => {
  if (canActivateDirectly) {
    return (
      <>
        <IconCreditCard /> Activate Event
      </>
    );
  }

  return (
    <>
      Next <IconArrowRight />
    </>
  );
};

const EventCycleInfo = () => {
  const event = useCurrentEvent();
  if (event == null) return null;
  const possibleBillableEvents = getBillableEventsForEvent(event);
  const pricing = getPricingRulesOfEvent(event);
  const billingCyclesInfo = getEventCyclesOfTimeRange(
    possibleBillableEvents,
    pricing.numDaysEventCycle,
  );

  return (
    <EventCycleInfoContainer>
      <Alert
        description={
          <>
            You are about to active a event with properly{' '}
            <b>{billingCyclesInfo.totalCycles} event cycles</b>.
          </>
        }
        showIcon
      />
    </EventCycleInfoContainer>
  );
};

const EventCycleInfoContainer = styled.section`
  margin: 10px;
  margin-bottom: 30px;
  font-size: 1.1rem;
`;

const ModalBody = styled.div`
  padding: 15px;
`;

const Disclaimer = () => {
  const user = useCurrentUser();
  const { canActivateDirectly } = useUserCanActivateDirectly(user?.id || undefined);
  const isSafemap = useIsSafemap();
  if (user == null) return null;

  return (
    <DisclaimerContainer>
      <p>
        {canActivateDirectly === false && 'By paying the Base Price you activate this event.'} The
        activation results in:
      </p>
      <ul>
        {isSafemap ? (
          <li>Share your Safemap among RACEMAP accounts.</li>
        ) : (
          <li>Removal of the watermarks.</li>
        )}
        <li>Devices and loads become chargeable.</li>
        <li>
          With each event cycle{' '}
          <PopoverHint variant="description-light">
            <EventCycleHint />
          </PopoverHint>{' '}
          map and used add-ons are charged.
        </li>
      </ul>

      <p>
        If you want to test live tracking or a specific feature, we recommend to keep the event as a
        free trial.
      </p>
    </DisclaimerContainer>
  );
};

const DisclaimerContainer = styled.section`
  margin-bottom: 1rem;

  ul {
    margin-bottom: 1rem;
    padding-left: 2rem;
  }
`;

const PayNowList = () => {
  const event = useCurrentEvent();
  const isSafemap = useIsSafemap();
  const { user: creator } = useUser({ userId: event?.creatorId });
  const eventBasePrice = useEventBasePrice(event?.type);

  if (creator == null || eventBasePrice == null) return null;

  return (
    <PayNowContainer
      message={
        <>
          <h5>Pay now</h5>
          <table>
            <thead>
              <tr>
                <th>Product</th>
                <th>Price</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Base Price, includes 1x {isSafemap ? 'Safemap' : 'Map'}</td>
                <td>{formatPrice(eventBasePrice)}</td>
              </tr>
            </tbody>
          </table>
        </>
      }
    />
  );
};

const PayNowContainer = styled(Alert)`
  margin-bottom: 1rem;

  h5 {
    font-size: 1.1rem;
    margin: 0 3px;
  }

  table {
    width: 100%;
    margin-top: 0.6rem;

    th {
      font-weight: 600;
      border-bottom: 1px solid ${RacemapColors.Gray};
      text-align: left;
    }
  }
`;

const FurtherInformation: FC<{ userId: string; eventId: string }> = ({ userId, eventId }) => {
  const isChild = useIsChildAccount();

  return (
    <FurtherInformationContainer>
      {!isChild && (
        <div>
          Event usage is tracked in <AnalyticsTabLink eventId={eventId} label="analytics" />, and
          the resulting costs are billed monthly. Review your monthly costs for all your events in
          the <BillingLink label="billing dashboard" userId={userId} openInNewTab />. For detailed
          pricing information, visit our <PricingLink label="pricing page" /> and use the{' '}
          <CostCalculatorLink label="calculator" />.
        </div>
      )}

      <div>
        You can archive an active event to halt additional charges. Archived events do not load the
        Map, Add-Ons, or provide any information. You can reactivate an archived event at any time.
      </div>
    </FurtherInformationContainer>
  );
};

const FurtherInformationContainer = styled.section`
  margin-bottom: 1rem;

  &>div:not(:last-of-type) {
    margin-bottom: 1rem;
  }
`;

const Checks: FC<{
  value: Checks;
  onChange: (newValue: Checks) => void;
}> = ({ value, onChange }) => {
  const handleChange = (key: keyof typeof value, newValue: boolean) => {
    onChange({ ...value, [key]: newValue });
  };

  return (
    <CheckContainer>
      <section>
        <FormCheckText
          id={'accept-terms'}
          value={value.acceptTerms}
          onChange={(value: boolean) => handleChange('acceptTerms', value)}
        >
          I read and understand the pricing for live tracking. I accept the{' '}
          <a href="/terms-of-use" target="_blank" rel="noreferrer noopener">
            terms of use
          </a>
          .
        </FormCheckText>
      </section>
      <section>
        <FormCheckText
          id={'accept-pricing'}
          value={value.acceptSubscription}
          onChange={(value: boolean) => handleChange('acceptSubscription', value)}
        >
          By paying the Base Price, I authorize Racemap to charge me for any potential subsequent
          usage on a monthly schedule.
        </FormCheckText>
      </section>
    </CheckContainer>
  );
};

const CheckContainer = styled.div`
  margin-top: 1rem;
  padding: 5px;

  &>section:not(:last-of-type) {
    margin-bottom: 1rem;
  }
`;
